import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { CheckCircleOutline as CheckIcon, LockReset as LockResetIcon } from '@mui/icons-material';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../services/firebase';
import { DEPLOYMENT_CONFIG } from '../config/deploymentConfig';

const PasswordResetAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get('oobCode') || '';
  const [verifying, setVerifying] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [complete, setComplete] = useState(false);
  const { appName, loginLogoPath } = DEPLOYMENT_CONFIG.labels;

  useEffect(() => {
    let cancelled = false;

    const verifyCode = async () => {
      try {
        setVerifying(true);
        setError('');

        if (!oobCode) {
          throw new Error('This password reset link is missing required information.');
        }

        const email = await verifyPasswordResetCode(auth, oobCode);
        if (!cancelled) {
          setVerifiedEmail(email);
        }
      } catch (err) {
        if (!cancelled) {
          setError('This password reset link is invalid or has expired. Request a new reset link from the sign-in page.');
        }
      } finally {
        if (!cancelled) {
          setVerifying(false);
        }
      }
    };

    verifyCode();

    return () => {
      cancelled = true;
    };
  }, [oobCode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setSubmitting(true);
      await confirmPasswordReset(auth, oobCode, password);
      setComplete(true);
    } catch (err) {
      setError('Unable to reset this password. Request a new reset link and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 420 }}>
      <Card elevation={3}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img
              src={loginLogoPath}
              alt={`${appName} Logo`}
              style={{
                width: '180px',
                maxWidth: '70%',
                height: 'auto',
                marginBottom: '16px'
              }}
              onError={(event) => {
                event.target.style.display = 'none';
              }}
            />
            <Typography variant="h5" component="h1" gutterBottom>
              Reset Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {appName}
            </Typography>
          </Box>

          {verifying && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!verifying && error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!verifying && complete && (
            <Stack spacing={2}>
              <Alert severity="success" icon={<CheckIcon />}>
                Your password has been reset.
              </Alert>
              <Button variant="contained" onClick={() => navigate('/', { replace: true })}>
                Return to sign in
              </Button>
            </Stack>
          )}

          {!verifying && !complete && verifiedEmail && (
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Enter a new password for {verifiedEmail}.
                </Typography>
                <TextField
                  label="New Password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                  fullWidth
                  disabled={submitting}
                />
                <TextField
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  autoComplete="new-password"
                  fullWidth
                  disabled={submitting}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <LockResetIcon />}
                  disabled={submitting || !password || !confirmPassword}
                >
                  {submitting ? 'Resetting...' : 'Reset password'}
                </Button>
              </Stack>
            </Box>
          )}

          {!verifying && !complete && !verifiedEmail && (
            <Button variant="contained" onClick={() => navigate('/', { replace: true })} fullWidth>
              Return to sign in
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PasswordResetAction;
